// 简化的数据持久化服务V2 - 不依赖外部服务
export class SimpleDataPersistenceServiceV2 {
  constructor() {
    this.storagePrefix = 'altverse_'
    this.version = '1.0.0'
    this.compressionEnabled = false
    this.encryptionEnabled = false
    this.syncInProgress = false
    this.lastSyncTime = null
    this.listeners = new Map()
    
    // 存储配置
    this.storageConfig = {
      maxSize: 50 * 1024 * 1024, // 50MB
      syncInterval: 30000, // 30秒
      maxRetries: 3,
      retryDelay: 1000
    }
    
    this.init()
  }

  // 初始化服务
  async init() {
    try {
      // 检查存储支持
      await this.checkStorageSupport()
      
      // 初始化IndexedDB
      await this.initIndexedDB()
      
      // 启动自动同步
      this.startAutoSync()
      
      // 启动存储监控
      this.startStorageMonitoring()
      
      console.log('✅ 简化数据持久化服务V2初始化完成')
    } catch (error) {
      console.error('❌ 简化数据持久化服务V2初始化失败:', error)
    }
  }

  // 检查存储支持
  async checkStorageSupport() {
    this.supports = {
      localStorage: this.checkLocalStorage(),
      sessionStorage: this.checkSessionStorage(),
      indexedDB: this.checkIndexedDB(),
      compression: false
    }
    
    console.log('📊 存储支持情况:', this.supports)
  }

  // 检查localStorage支持
  checkLocalStorage() {
    try {
      const test = 'test'
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      return true
    } catch (e) {
      return false
    }
  }

  // 检查sessionStorage支持
  checkSessionStorage() {
    try {
      const test = 'test'
      sessionStorage.setItem(test, test)
      sessionStorage.removeItem(test)
      return true
    } catch (e) {
      return false
    }
  }

  // 检查IndexedDB支持
  checkIndexedDB() {
    return typeof indexedDB !== 'undefined'
  }

  // 初始化IndexedDB
  async initIndexedDB() {
    if (!this.supports.indexedDB) {
      console.warn('IndexedDB不支持，使用localStorage')
      return
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open('AltVerseSimpleDataV2', 1)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        console.log('✅ IndexedDB初始化成功')
        resolve()
      }
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        console.log('🔄 IndexedDB升级中...')
        
        // 删除旧的对象存储（如果存在）
        if (db.objectStoreNames.contains('userData')) {
          db.deleteObjectStore('userData')
        }
        
        // 创建新的对象存储
        const store = db.createObjectStore('userData', { keyPath: 'id' })
        
        // 创建索引（可选，用于优化查询）
        try {
          store.createIndex('userId', 'userId', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
          console.log('✅ IndexedDB索引创建成功')
        } catch (error) {
          console.warn('⚠️ IndexedDB索引创建失败，将使用游标查询:', error)
        }
      }
    })
  }

  // 保存数据
  async saveData(key, data, options = {}) {
    try {
      const {
        userId = 'guest',
        sync = true
      } = options

      const dataToSave = {
        id: key,
        userId,
        data,
        timestamp: Date.now(),
        version: this.version
      }

      // 保存到IndexedDB
      if (this.supports.indexedDB && this.db) {
        await this.saveToIndexedDB(dataToSave)
      }

      // 保存到localStorage作为备份
      if (this.supports.localStorage) {
        this.saveToLocalStorage(key, dataToSave)
      }

      // 触发同步
      if (sync) {
        this.triggerSync(key, dataToSave)
      }

      // 通知监听器
      this.notifyListeners('dataSaved', { key, data: dataToSave })

      return { success: true, key, timestamp: dataToSave.timestamp }
    } catch (error) {
      console.error('❌ 保存数据失败:', error)
      return { success: false, error: error.message }
    }
  }

  // 加载数据
  async loadData(key, options = {}) {
    try {
      const { userId = 'guest', fallback = true } = options

      let data = null

      // 从IndexedDB加载
      if (this.supports.indexedDB && this.db) {
        data = await this.loadFromIndexedDB(key, userId)
      }

      // 如果IndexedDB失败，从localStorage加载
      if (!data && fallback && this.supports.localStorage) {
        data = this.loadFromLocalStorage(key)
      }

      if (!data) {
        return { success: false, error: '数据不存在' }
      }

      // 通知监听器
      this.notifyListeners('dataLoaded', { key, data })

      return { success: true, data: data.data }
    } catch (error) {
      console.error('❌ 加载数据失败:', error)
      return { success: false, error: error.message }
    }
  }

  // 保存到IndexedDB
  async saveToIndexedDB(dataToSave) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['userData'], 'readwrite')
      const store = transaction.objectStore('userData')
      
      const request = store.put(dataToSave)
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // 从IndexedDB加载
  async loadFromIndexedDB(key, userId) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['userData'], 'readonly')
      const store = transaction.objectStore('userData')
      
      const request = store.get(key)
      
      request.onsuccess = () => {
        const result = request.result
        if (result && result.userId === userId) {
          resolve(result)
        } else {
          resolve(null)
        }
      }
      
      request.onerror = () => reject(request.error)
    })
  }

  // 保存到localStorage
  saveToLocalStorage(key, data) {
    try {
      const storageKey = `${this.storagePrefix}${key}`
      localStorage.setItem(storageKey, JSON.stringify(data))
    } catch (error) {
      console.error('❌ localStorage保存失败:', error)
    }
  }

  // 从localStorage加载
  loadFromLocalStorage(key) {
    try {
      const storageKey = `${this.storagePrefix}${key}`
      const data = localStorage.getItem(storageKey)
      return data ? JSON.parse(data) : null
    } catch (error) {
      console.error('❌ localStorage加载失败:', error)
      return null
    }
  }

  // 获取所有数据
  async getAllData(userId = 'guest') {
    try {
      const allData = {
        stories: [],
        characters: [],
        chapters: [],
        media: [],
        settings: {}
      }

      if (this.supports.indexedDB && this.db) {
        const data = await this.getAllFromIndexedDB(userId)
        
        // 分类数据
        data.forEach(item => {
          const key = item.id
          if (key.startsWith('story_')) {
            allData.stories.push(item.data)
          } else if (key.startsWith('character_')) {
            allData.characters.push(item.data)
          } else if (key.startsWith('chapter_')) {
            allData.chapters.push(item.data)
          } else if (key.startsWith('media_')) {
            allData.media.push(item.data)
          } else if (key.startsWith('settings_')) {
            allData.settings = item.data
          }
        })
      }

      return { success: true, data: allData }
    } catch (error) {
      console.error('❌ 获取所有数据失败:', error)
      return { success: false, error: error.message }
    }
  }

  // 从IndexedDB获取所有数据
  async getAllFromIndexedDB(userId) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['userData'], 'readonly')
      const store = transaction.objectStore('userData')
      
      // 使用游标遍历所有数据，然后过滤userId
      const request = store.openCursor()
      const results = []
      
      request.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          const data = cursor.value
          if (data.userId === userId) {
            results.push(data)
          }
          cursor.continue()
        } else {
          resolve(results)
        }
      }
      
      request.onerror = () => reject(request.error)
    })
  }

  // 数据同步
  async syncData(userId = 'guest') {
    if (this.syncInProgress) {
      console.log('⏳ 同步已在进行中...')
      return
    }

    this.syncInProgress = true
    
    try {
      console.log('🔄 开始数据同步...')
      
      // 获取本地数据
      const localData = await this.getAllData(userId)
      
      if (!localData.success) {
        throw new Error(localData.error)
      }

      // 更新同步时间
      this.lastSyncTime = Date.now()
      
      // 通知监听器
      this.notifyListeners('dataSynced', { 
        timestamp: this.lastSyncTime,
        data: localData.data 
      })

      console.log('✅ 数据同步完成')
      return { success: true, timestamp: this.lastSyncTime }
    } catch (error) {
      console.error('❌ 数据同步失败:', error)
      return { success: false, error: error.message }
    } finally {
      this.syncInProgress = false
    }
  }

  // 启动自动同步
  startAutoSync() {
    setInterval(() => {
      this.syncData()
    }, this.storageConfig.syncInterval)
  }

  // 触发同步
  triggerSync(key, data) {
    // 防抖，避免频繁同步
    clearTimeout(this.syncTimeout)
    this.syncTimeout = setTimeout(() => {
      this.syncData()
    }, 1000)
  }

  // 启动存储监控
  startStorageMonitoring() {
    // 监控存储使用情况
    setInterval(() => {
      this.checkStorageUsage()
    }, 60000) // 每分钟检查一次
  }

  // 检查存储使用情况
  checkStorageUsage() {
    try {
      if (this.supports.localStorage) {
        let totalSize = 0
        for (let key in localStorage) {
          if (key.startsWith(this.storagePrefix)) {
            totalSize += localStorage[key].length
          }
        }
        
        const usagePercent = (totalSize / this.storageConfig.maxSize) * 100
        
        if (usagePercent > 80) {
          console.warn(`⚠️ 存储使用率过高: ${usagePercent.toFixed(2)}%`)
          this.notifyListeners('storageWarning', { usagePercent, totalSize })
        }
      }
    } catch (error) {
      console.error('❌ 存储监控失败:', error)
    }
  }

  // 添加监听器
  addListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
  }

  // 移除监听器
  removeListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback)
    }
  }

  // 通知监听器
  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('❌ 监听器执行失败:', error)
        }
      })
    }
  }

  // 获取存储统计信息
  async getStorageStats() {
    try {
      const stats = {
        totalSize: 0,
        itemCount: 0,
        lastSync: this.lastSyncTime,
        compressionEnabled: this.compressionEnabled,
        encryptionEnabled: this.encryptionEnabled,
        supports: this.supports
      }

      if (this.supports.localStorage) {
        for (let key in localStorage) {
          if (key.startsWith(this.storagePrefix)) {
            stats.totalSize += localStorage[key].length
            stats.itemCount++
          }
        }
      }

      return { success: true, stats }
    } catch (error) {
      console.error('❌ 获取存储统计失败:', error)
      return { success: false, error: error.message }
    }
  }
}

// 创建单例实例
const simpleDataPersistenceServiceV2 = new SimpleDataPersistenceServiceV2()

export default simpleDataPersistenceServiceV2

